import {
  Checkbox,
  Grid,
  makeStyles,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { IProductItemToBilling } from "api/interfaces";
import { ITableColumn } from "components/interfaces";
import MaskedField from "components/UI/Fields/MaskedField";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { SendToBillingStore as Store } from "store";
import { decimal } from "utils/DecimalPrecision";
import { lang } from "../../../i18nLang";

const useStyles = makeStyles(() => ({
  description: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    height: 24,
    width: 138,
  },
  input: { width: 180 },
}));

/** Linha da tabela de Seleção de Itens do Faturamento. */
const ItemsTableRow = observer(
  ({
    rowData,
    columns,
    disableInputs = false,
    apportiongTotal,
  }: {
    /** Configuração das colunas. */
    columns: ITableColumn[];
    /** Dados da linha. */
    rowData: IProductItemToBilling;
    /** Define se os inputs devem ou não ser desabilitados. */
    disableInputs: boolean;
    /** Total de rateio já aplicado nos itens da tabela. */
    apportiongTotal: number;
  }) => {
    const classes = useStyles();
    const [data, setData] = useState(rowData);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
      setData(rowData);
    }, [rowData, rowData.apportionmentValue]);

    useEffect(() => {
      if (!Store.productsTotalIsGreaterThanTotalMeasured()) {
        setHasError(false);
      }
    }, [apportiongTotal]);

    const handleClickRow = () => {
      const newData = Store.selectProdutctItemAndUpdateProductsApportionment(
        data.codItem,
        !data.selected
      );
      if (newData) setData({ ...newData });
    };

    const handleChangeMaterialUsed = (checked: boolean) => {
      const newData = Store.setProductsMaterialUsed(data.codItem, checked);
      if (newData) setData({ ...newData });
    };

    const handleChangeApportioningInputs = (event) => {
      const { value, name } = event.target;
      if (decimal(data[name], 2).isEqualTo(value)) return;

      const newData = { ...data };
      newData[name] = value;
      if (newData) setData({ ...newData });
    };

    const handleKeyDownApportionmentValue = (ev) => {
      ev.stopPropagation();
      const key = ev.key;
      if (key === "Enter") {
        handleBlurApportionmentValue();
      }
    };

    const handleBlurApportionmentValue = () => {
      Store.setProductsApportionmentValue(
        data.codItem,
        data.apportionmentValue || 0
      );
      const newData = Store.calculateProductsMeasurementValue(data.codItem);
      if (newData) setData({ ...newData });

      Store.calculateProductsItemsApportionmentTotal();
      setHasError(Store.productsTotalIsGreaterThanTotalMeasured());
    };

    const handleKeyDownApportionmentPercentage = (ev) => {
      ev.stopPropagation();
      const key = ev.key;
      if (key === "Enter") {
        handleBlurApportionmentPercentage();
      }
    };

    const handleBlurApportionmentPercentage = () => {
      Store.setProductsApportionmentPercentage(
        data.codItem,
        data.apportionmentPercentage || 0
      );
      const newData = Store.calculateProductsMeasurementValue(data.codItem);
      if (newData) setData({ ...newData });

      Store.calculateProductsItemsApportionmentTotal();
      setHasError(Store.productsTotalIsGreaterThanTotalMeasured());
    };

    const stopClickPropagation = (event) => event.stopPropagation();

    return (
      <TableRow
        key={data.codItem}
        tabIndex={-1}
        role="checkbox"
        hover
        aria-checked={data.selected}
        selected={data.selected}
        onClick={(event) => handleClickRow()}
      >
        <TableCell component="th" scope="row" padding={"checkbox"}>
          <Checkbox checked={data.selected} color="primary" />
        </TableCell>
        <TableCell onClick={stopClickPropagation}>{data.codItem}</TableCell>
        <TableCell width={columns[2].width} onClick={stopClickPropagation}>
          <Tooltip title={data.description}>
            <Typography className={classes.description}>
              {data.description}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell onClick={stopClickPropagation}>{data.unit.code}</TableCell>
        <TableCell
          padding={"checkbox"}
          width={columns[4].width}
          onClick={stopClickPropagation}
        >
          <Grid container justifyContent="center">
            <Checkbox
              checked={data.materialUsed}
              color="primary"
              onChange={(event, checked) => handleChangeMaterialUsed(checked)}
              disabled={!data.selected}
            />
          </Grid>
        </TableCell>
        <TableCell width={columns[5].width} onClick={stopClickPropagation}>
          <Grid container alignItems="center" justifyContent="space-evenly">
            <MaskedField
              label={lang("fields.value")}
              name="apportionmentValue"
              className={classes.input}
              margin="none"
              prefix={`${lang("currency")} `}
              fixedDecimalScale
              allowNegative={false}
              value={data.apportionmentValue}
              disabled={disableInputs || !data.selected}
              onChange={handleChangeApportioningInputs}
              onBlur={handleBlurApportionmentValue}
              onKeyDown={handleKeyDownApportionmentValue}
              error={hasError}
              helperText={hasError ? lang("billing.valueErrorMessage") : ""}
            />
            <Typography>{lang("or")}</Typography>
            <MaskedField
              label={lang("fields.percentage")}
              name="apportionmentPercentage"
              className={classes.input}
              margin="none"
              suffix="%"
              allowNegative={false}
              value={data.apportionmentPercentage}
              disabled={disableInputs || !data.selected}
              onChange={handleChangeApportioningInputs}
              onBlur={handleBlurApportionmentPercentage}
              onKeyDown={handleKeyDownApportionmentPercentage}
              error={hasError}
              helperText={
                hasError ? lang("billing.percentageErrorMessage") : ""
              }
            />
          </Grid>
        </TableCell>
      </TableRow>
    );
  }
);

export default ItemsTableRow;
